import { useState } from 'react';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../../auth/AuthProvider';
import { errorMessage, fileUrl } from '../../api/client';
import { useDeleteUser, useInviteUser, useResendInvite, useUpdateUser, useUsers, type UserRow } from '../../features/users/api';
import { tableLocale, tableLoading } from '../../components/states';
import { fmtDateTime } from '../../utils/format';
import { NameCell } from '../../components/NameCell';

const statusColor: Record<UserRow['status'], string> = { ACTIVE: 'success', INVITED: 'warning', DISABLED: 'default' };
const statusLabel: Record<UserRow['status'], string> = { ACTIVE: 'Active', INVITED: 'Invited', DISABLED: 'Disabled' };

export function UsersPage() {
  const { user: me } = useAuth();
  const { message, modal } = App.useApp();
  const users = useUsers();
  const invite = useInviteUser();
  const resend = useResendInvite();
  const update = useUpdateUser();
  const remove = useDeleteUser();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();

  const showInviteLink = (link: string, email: string, emailError: string | null) =>
    (emailError ? modal.warning : modal.info)({
      title: emailError ? 'Invite created, but the email was not sent' : 'Invite sent',
      content: (
        <div>
          {emailError ? (
            <Typography.Paragraph>
              The email to <b>{email}</b> failed: <Typography.Text type="danger">{emailError}</Typography.Text>. Share this link
              directly, or fix the email settings and use Resend invite:
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph>
              An invite email was sent to <b>{email}</b>. If email is not configured yet, share this link directly:
            </Typography.Paragraph>
          )}
          <Typography.Paragraph copyable code style={{ wordBreak: 'break-all' }}>
            {link}
          </Typography.Paragraph>
        </div>
      ),
    });

  const onInvite = async (values: { name: string; email: string; role: 'ADMIN' | 'SUPER_ADMIN' }) => {
    try {
      const r = await invite.mutateAsync(values);
      setInviteOpen(false);
      form.resetFields();
      showInviteLink(r.inviteLink, r.email, r.emailError);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const act = async (p: Promise<unknown>, ok: string) => {
    try {
      await p;
      message.success(ok);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Users"
        subtitle="Admins who can sign in. Only super admins manage users and system settings."
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
            Invite user
          </Button>
        }
      />
      <Card>
      <Table<UserRow>
        rowKey="id"
        loading={tableLoading(users)}
        locale={tableLocale(users, 'No users yet.')}
        dataSource={users.data ?? []}
        pagination={false}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            render: (v: string, r) => (
              <Space size={8}>
                <NameCell name={v || r.email} sub={r.email} image={fileUrl(r.photoFileId)} />
                {r.id === me?.id && <Tag color="processing">You</Tag>}
              </Space>
            ),
          },
          {
            title: 'Role',
            dataIndex: 'role',
            render: (role: UserRow['role'], r) => (
              <Select
                size="small"
                value={role}
                disabled={r.id === me?.id}
                style={{ width: 140 }}
                options={[{ value: 'ADMIN', label: 'Admin' }, { value: 'SUPER_ADMIN', label: 'Super admin' }]}
                onChange={(v) => void act(update.mutateAsync({ id: r.id, role: v }), 'Role updated')}
              />
            ),
          },
          { title: 'Status', dataIndex: 'status', render: (s: UserRow['status']) => <Tag color={statusColor[s]}>{statusLabel[s]}</Tag> },
          { title: 'Last login', dataIndex: 'lastLoginAt', render: (v: string | null) => (v ? fmtDateTime(v) : 'Never') },
          {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, r) => (
              <Space>
                {r.status === 'INVITED' && (
                  <Button size="small" loading={resend.isPending && resend.variables === r.id} disabled={resend.isPending && resend.variables !== r.id} onClick={() => void resend.mutateAsync(r.id).then((x) => { showInviteLink(x.inviteLink, r.email, x.emailError); }).catch((e) => message.error(errorMessage(e)))}>
                    Resend invite
                  </Button>
                )}
                {r.status === 'ACTIVE' && r.id !== me?.id && (
                  <Button size="small" onClick={() => void act(update.mutateAsync({ id: r.id, status: 'DISABLED' }), 'User disabled')}>
                    Disable
                  </Button>
                )}
                {r.status === 'DISABLED' && (
                  <Button size="small" onClick={() => void act(update.mutateAsync({ id: r.id, status: 'ACTIVE' }), 'User re-enabled')}>
                    Enable
                  </Button>
                )}
                {r.id !== me?.id && (
                  <Popconfirm title={`Delete ${r.email}?`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void act(remove.mutateAsync(r.id), 'User deleted')}>
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Modal
        title="Invite a user"
        open={inviteOpen}
        onCancel={() => { setInviteOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Send invite"
        confirmLoading={invite.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={onInvite} initialValues={{ role: 'ADMIN' }} requiredMark={false}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select options={[{ value: 'ADMIN', label: 'Admin' }, { value: 'SUPER_ADMIN', label: 'Super admin' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
